import React, { useState, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import {
    HomeIcon,
    BuildingOffice2Icon,
    StarIcon,
    ShieldCheckIcon,
    CurrencyDollarIcon,
    ClockIcon,
    DocumentCheckIcon,
    CheckCircleIcon,
    SparklesIcon,
    BoltIcon,
    HeartIcon,
    LightBulbIcon,
    RocketLaunchIcon,
    ChartBarIcon,
    UserGroupIcon,
    BanknotesIcon,
    KeyIcon
} from '@heroicons/react/24/outline';

// Icon options
const iconOptions = [
    { name: 'StarIcon', icon: StarIcon, label: 'Star' },
    { name: 'HomeIcon', icon: HomeIcon, label: 'Home' },
    { name: 'BuildingOffice2Icon', icon: BuildingOffice2Icon, label: 'Building' },
    { name: 'ShieldCheckIcon', icon: ShieldCheckIcon, label: 'Security' },
    { name: 'CurrencyDollarIcon', icon: CurrencyDollarIcon, label: 'Price/EMI' },
    { name: 'ClockIcon', icon: ClockIcon, label: 'Time/Speed' },
    { name: 'DocumentCheckIcon', icon: DocumentCheckIcon, label: 'Documentation' },
    { name: 'CheckCircleIcon', icon: CheckCircleIcon, label: 'Approval' },
    { name: 'SparklesIcon', icon: SparklesIcon, label: 'Features' },
    { name: 'BoltIcon', icon: BoltIcon, label: 'Fast/Quick' },
    { name: 'HeartIcon', icon: HeartIcon, label: 'Trust' },
    { name: 'LightBulbIcon', icon: LightBulbIcon, label: 'Idea' },
    { name: 'RocketLaunchIcon', icon: RocketLaunchIcon, label: 'Launch' },
    { name: 'ChartBarIcon', icon: ChartBarIcon, label: 'Growth' },
    { name: 'UserGroupIcon', icon: UserGroupIcon, label: 'Support' },
    { name: 'BanknotesIcon', icon: BanknotesIcon, label: 'Money' },
    { name: 'KeyIcon', icon: KeyIcon, label: 'Access' }
];

// Gradient color options
const gradientOptions = [
    { from: 'blue-500', to: 'indigo-600', label: 'Blue' },
    { from: 'green-500', to: 'emerald-600', label: 'Green' },
    { from: 'purple-500', to: 'violet-600', label: 'Purple' },
    { from: 'orange-500', to: 'red-600', label: 'Orange' },
    { from: 'teal-500', to: 'cyan-600', label: 'Teal' },
    { from: 'pink-500', to: 'rose-600', label: 'Pink' }
];

const KeyBenefitModal = ({ isOpen, onClose, onSubmit, benefit, loading, loanType }) => {
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        icon_name: 'StarIcon',
        gradient_from: 'blue-500',
        gradient_to: 'indigo-600',
        display_order: 0,
        is_active: true,
        loan_type: loanType
    });

    useEffect(() => {
        if (benefit) {
            setFormData({
                ...benefit,
                is_active: benefit.is_active === 1 || benefit.is_active === true
            });
        } else {
            setFormData({
                title: '',
                description: '',
                icon_name: 'StarIcon',
                gradient_from: 'blue-500',
                gradient_to: 'indigo-600',
                display_order: 0,
                is_active: true,
                loan_type: loanType
            });
        }
    }, [benefit, isOpen, loanType]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleGradientSelect = (gradient) => {
        setFormData(prev => ({
            ...prev,
            gradient_from: gradient.from,
            gradient_to: gradient.to
        }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        // Ensure loan_type is always set from props, as a fallback or override
        onSubmit({ ...formData, loan_type: loanType });
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="sticky top-0 bg-white border-b border-slate-200 px-8 py-6 flex items-center justify-between rounded-t-3xl">
                    <h2 className="text-2xl font-bold text-slate-800">
                        {benefit ? 'Edit Benefit' : 'Add New Benefit'}
                    </h2>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-slate-100 rounded-xl transition-colors"
                    >
                        <XMarkIcon className="h-6 w-6 text-slate-500" />
                    </button>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="p-8 space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Title */}
                        <div className="md:col-span-2">
                            <label className="block text-sm font-semibold text-slate-700 mb-2">
                                Benefit Title *
                            </label>
                            <input
                                type="text"
                                name="title"
                                value={formData.title}
                                onChange={handleChange}
                                required
                                className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                                placeholder="e.g., Quick Approval"
                            />
                        </div>

                        {/* Description */}
                        <div className="md:col-span-2">
                            <label className="block text-sm font-semibold text-slate-700 mb-2">
                                Description *
                            </label>
                            <textarea
                                name="description"
                                value={formData.description}
                                onChange={handleChange}
                                required
                                rows={3}
                                className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all resize-none"
                                placeholder="Brief description of the benefit"
                            />
                        </div>

                        {/* Icon Selection */}
                        <div className="md:col-span-2">
                            <label className="block text-sm font-semibold text-slate-700 mb-2">
                                Select Icon *
                            </label>
                            <div className="grid grid-cols-4 sm:grid-cols-6 gap-3">
                                {iconOptions.map((option) => {
                                    const Icon = option.icon;
                                    return (
                                        <button
                                            key={option.name}
                                            type="button"
                                            onClick={() => setFormData(prev => ({ ...prev, icon_name: option.name }))}
                                            className={`flex flex-col items-center p-3 rounded-xl border-2 transition-all ${formData.icon_name === option.name
                                                ? 'border-primary-600 bg-primary-50 text-primary-600'
                                                : 'border-slate-100 hover:border-slate-200 text-slate-500'
                                                }`}
                                            title={option.label}
                                        >
                                            <Icon className="h-6 w-6" />
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Gradient Selection */}
                        <div className="md:col-span-2">
                            <label className="block text-sm font-semibold text-slate-700 mb-2">
                                Card Theme *
                            </label>
                            <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
                                {gradientOptions.map((gradient) => (
                                    <button
                                        key={gradient.label}
                                        type="button"
                                        onClick={() => handleGradientSelect(gradient)}
                                        className={`relative h-12 rounded-xl bg-gradient-to-r from-${gradient.from} to-${gradient.to} border-2 transition-all ${formData.gradient_from === gradient.from && formData.gradient_to === gradient.to
                                            ? 'border-slate-800 scale-105 shadow-md'
                                            : 'border-transparent hover:scale-105'
                                            }`}
                                    >
                                        <span className="sr-only">{gradient.label}</span>
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Display Order */}
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-2">
                                Display Order
                            </label>
                            <input
                                type="number"
                                name="display_order"
                                value={formData.display_order}
                                onChange={handleChange}
                                min="0"
                                className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                            />
                        </div>

                        {/* Active Toggle */}
                        <div className="flex items-center">
                            <label className="flex items-center space-x-3 cursor-pointer">
                                <input
                                    type="checkbox"
                                    name="is_active"
                                    checked={formData.is_active}
                                    onChange={handleChange}
                                    className="w-5 h-5 text-primary-600 border-slate-300 rounded focus:ring-primary-500 transition-all"
                                />
                                <span className="text-sm font-semibold text-slate-700">Active Status</span>
                            </label>
                        </div>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-4 pt-6 border-t border-slate-200">
                        <button
                            type="button"
                            onClick={onClose}
                            className="flex-1 px-6 py-3 border border-slate-200 text-slate-700 rounded-xl hover:bg-slate-50 transition-colors font-medium"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className="flex-1 px-6 py-3 bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-colors font-medium disabled:opacity-50 shadow-lg shadow-primary-600/20"
                        >
                            {loading ? 'Saving...' : benefit ? 'Update Benefit' : 'Create Benefit'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default KeyBenefitModal;
